/* global process */
/**
 * DEV-ONLY tenant replication override.
 *
 * Lets a local dev session boot as any tenant by seeding `client` + `system_settings`
 * from a captured snapshot, so tenant-gated branches (isLegacyCalendar, newProgramInterface,
 * enableThirdPartyIntegrations effects, showKidsMode, ...) render without needing that
 * tenant's real account on the backend.
 *
 * Consumed at the two store chokepoints — `organization.getClient/getTenant` and
 * `systemSettings.getSystemSettings` — so every downstream hook (useOrganization,
 * useSystemSettings) resolves against the override transparently.
 *
 * SAFETY: returns null unless BOTH
 *   1) NODE_ENV !== 'production', and
 *   2) an explicit `__tenantOverride` key exists in localStorage.
 * It can never activate in a production build. Set it with the `replicate-tenant` skill.
 *
 * The localStorage value is read once per page load (cached), so changing the override
 * requires a reload — which the app does anyway when the store re-initialises.
 *
 * Shape of the stored JSON (any field optional; missing fields fall back to the real store):
 *   { "client": "EDELVIVES_DIGITAL_PLUS", "tenantData": {...}, "systemSettings": { "enable_library": 1, ... } }
 */
export const TENANT_OVERRIDE_KEY = '__tenantOverride';

let cached;

export function getTenantOverride() {
  if (process.env.NODE_ENV === 'production') return null;
  if (cached !== undefined) return cached;

  try {
    const raw = globalThis.localStorage?.getItem(TENANT_OVERRIDE_KEY);
    cached = raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('[tenantOverride] ignoring malformed __tenantOverride value', error);
    cached = null;
  }

  if (cached) {
    console.info('[tenantOverride] DEV tenant replication active:', cached.client ?? '(system_settings only)');
  }

  return cached;
}
